import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Save, Wand2, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const MONTH_NAMES_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const MONTH_NAMES_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function formatMoney(value, currency = "EUR") {
  try {
    return new Intl.NumberFormat("es-ES", { style: "currency", currency, maximumFractionDigits: 2, useGrouping: "always" }).format(value || 0);
  } catch {
    return `${(value || 0).toFixed(2)} ${currency}`;
  }
}

export default function Budget() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const baseCurrency = user?.base_currency || "EUR";
  const monthNames = lang === "es" ? MONTH_NAMES_ES : MONTH_NAMES_EN;
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [rows, setRows] = useState([]);
  const [annual, setAnnual] = useState(null);
  const [ytd, setYtd] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [templateOpen, setTemplateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/budget", { params: { year } });
      setRows(data.months);
      setAnnual(data.annual);
      setYtd(data.ytd || null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const updateRow = (month, key, value) => {
    setRows((prev) => prev.map((r) => r.month === month ? { ...r, [key]: value === "" ? null : Number(value) } : r));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        year,
        months: rows.map((r) => ({
          month: r.month,
          revenue_target: r.revenue_target || 0,
          food_cost_pct_target: r.food_cost_pct_target,
          salary_pct_target: r.salary_pct_target,
        })),
      };
      await api.put("/budget", payload);
      toast.success(lang === "es" ? "Presupuesto guardado" : "Budget saved");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const applyTemplate = (templateRows) => {
    setRows((prev) => prev.map((r) => {
      const incoming = templateRows.find((tr) => tr.month === r.month);
      if (!incoming) return r;
      return {
        ...r,
        revenue_target: incoming.revenue_target,
        food_cost_pct_target: incoming.food_cost_pct_target ?? r.food_cost_pct_target,
        salary_pct_target: incoming.salary_pct_target ?? r.salary_pct_target,
      };
    }));
    setTemplateOpen(false);
    toast.success(lang === "es" ? "Plantilla aplicada (recuerda guardar)" : "Template applied (remember to save)");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-[var(--text-muted)] mb-2">{lang === "es" ? "Planificación" : "Planning"}</p>
          <h1 className="font-display text-4xl font-bold tracking-tight leading-none">
            {lang === "es" ? "Presupuesto" : "Budget"}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-2xl">
            {lang === "es"
              ? "Define tu objetivo mensual de facturación, % Food Cost y % Coste Personal. La app compara automáticamente con los datos reales."
              : "Set your monthly revenue target, Food Cost % and Staff Cost %. The app compares them with your actual figures."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <YearStepper year={year} onChange={setYear} />
          <Dialog open={templateOpen} onOpenChange={setTemplateOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="budget-template-btn">
                <Wand2 size={14} className="mr-1.5" /> {lang === "es" ? "Plantilla" : "Template"}
              </Button>
            </DialogTrigger>
            <TemplateDialog year={year} onApply={applyTemplate} lang={lang} />
          </Dialog>
          <Button variant="outline" onClick={load} data-testid="budget-refresh-btn">
            <RefreshCw size={14} className="mr-1.5" /> {lang === "es" ? "Actualizar" : "Refresh"}
          </Button>
          <Button onClick={save} disabled={saving} data-testid="budget-save-btn" className="bg-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/90 text-white">
            <Save size={14} className="mr-1.5" /> {saving ? "..." : lang === "es" ? "Guardar" : "Save"}
          </Button>
        </div>
      </div>

      <AnnualSummary annual={annual} baseCurrency={baseCurrency} lang={lang} />
      <YtdSummary ytd={ytd} baseCurrency={baseCurrency} lang={lang} />

      <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-3 overflow-x-auto">
        <table className="w-full text-sm" data-testid="budget-table">
          <thead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
            <tr className="border-b border-[var(--border-color)]">
              <th className="text-center py-2 px-2 font-medium" style={{ width: 90 }}>{lang === "es" ? "Mes" : "Month"}</th>
              <th className="text-center py-2 px-2 font-medium" style={{ width: 140 }}>{lang === "es" ? "Objetivo €" : "Target €"}</th>
              <th className="text-center py-2 px-2 font-medium" style={{ width: 140 }}>{lang === "es" ? "Real €" : "Actual €"}</th>
              <th className="text-center py-2 px-2 font-medium" style={{ minWidth: 180 }}>{lang === "es" ? "Cumplimiento" : "Achievement"}</th>
              <th className="text-center py-2 px-2 font-medium" style={{ width: 130 }}>{lang === "es" ? "Food Cost % obj." : "Food Cost % target"}</th>
              <th className="text-center py-2 px-2 font-medium" style={{ width: 140 }}>{lang === "es" ? "Food Cost % real" : "Food Cost % actual"}</th>
              <th className="text-center py-2 px-2 font-medium" style={{ width: 130 }}>{lang === "es" ? "Personal % obj." : "Staff % target"}</th>
              <th className="text-center py-2 px-2 font-medium" style={{ width: 140 }}>{lang === "es" ? "Personal % real" : "Staff % actual"}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="8" className="text-center py-8 text-[var(--text-muted)]">…</td></tr>
            )}
            {!loading && rows.map((r) => (
              <BudgetRow
                key={r.month}
                row={r}
                monthName={monthNames[r.month - 1]}
                onChange={updateRow}
                baseCurrency={baseCurrency}
              />
            ))}
          </tbody>
          {!loading && rows.length > 0 && (
            <BudgetTotalsFooter rows={rows} baseCurrency={baseCurrency} lang={lang} />
          )}
        </table>
      </div>
    </div>
  );
}

function YearStepper({ year, onChange }) {
  return (
    <div className="inline-flex items-center bg-[var(--surface)] border border-[var(--border-color)] rounded-md">
      <button
        onClick={() => onChange(year - 1)}
        className="px-2 py-1.5 hover:bg-[var(--surface-hover)] transition-colors text-[var(--text-secondary)]"
        data-testid="budget-year-prev"
        aria-label="Previous year"
      >
        <ChevronLeft size={14} />
      </button>
      <span className="px-3 font-mono-num font-semibold" data-testid="budget-year-label">{year}</span>
      <button
        onClick={() => onChange(year + 1)}
        className="px-2 py-1.5 hover:bg-[var(--surface-hover)] transition-colors text-[var(--text-secondary)]"
        data-testid="budget-year-next"
        aria-label="Next year"
      >
        <ChevronRight size={14} />
      </button>
    </div>
  );
}

function AnnualSummary({ annual, baseCurrency, lang }) {
  if (!annual) {
    return <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg h-24 animate-pulse" />;
  }
  const ach = annual.revenue_achievement_pct;
  const achColor = ach == null
    ? "text-[var(--text-muted)]"
    : ach >= 100
      ? "text-[var(--status-positive)]"
      : ach >= 90
        ? "text-[var(--status-warning)]"
        : "text-[var(--status-negative)]";
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="budget-annual-summary">
      <SummaryCard label={lang === "es" ? "Objetivo anual" : "Annual target"} value={formatMoney(annual.revenue_target, baseCurrency)} testid="annual-target" />
      <SummaryCard label={lang === "es" ? "Real anual" : "Annual actual"} value={formatMoney(annual.revenue_actual, baseCurrency)} testid="annual-actual" />
      <SummaryCard
        label={lang === "es" ? "Diferencia" : "Difference"}
        value={formatMoney(annual.revenue_diff, baseCurrency)}
        accent={annual.revenue_diff >= 0 ? "positive" : "negative"}
        testid="annual-diff"
      />
      <SummaryCard
        label={lang === "es" ? "Cumplimiento" : "Achievement"}
        value={ach == null ? "—" : `${ach}%`}
        valueClass={achColor}
        testid="annual-achievement"
      />
      <SummaryCard
        label={lang === "es" ? "Food Cost / Personal real" : "Food Cost / Staff actual"}
        value={`${annual.food_cost_pct_actual ?? "—"}% / ${annual.salary_pct_actual ?? "—"}%`}
        testid="annual-ratios"
      />
    </div>
  );
}

function YtdSummary({ ytd, baseCurrency, lang }) {
  if (!ytd) return null;
  const months = ytd.months_elapsed || 0;
  const ach = ytd.revenue_achievement_pct;
  const diffAccent = (ytd.revenue_diff || 0) >= 0 ? "positive" : "negative";
  return (
    <div data-testid="ytd-summary" className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold tracking-tight">
          {lang === "es" ? `Acumulado del año (YTD · ${months} mes${months === 1 ? "" : "es"})` : `Year to date (${months} month${months === 1 ? "" : "s"})`}
        </h2>
        {ach != null && (
          <span className={`text-xs font-mono-num px-2 py-1 rounded ${ach >= 100 ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" : "bg-[var(--status-warning-bg)] text-[var(--status-warning)]"}`} data-testid="ytd-achievement">
            {ach}% {lang === "es" ? "del objetivo" : "of target"}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <SummaryCard
          label={lang === "es" ? "Objetivo YTD" : "Target YTD"}
          value={formatMoney(ytd.revenue_target, baseCurrency)}
          testid="ytd-target"
        />
        <SummaryCard
          label={lang === "es" ? "Real YTD" : "Actual YTD"}
          value={formatMoney(ytd.revenue_actual, baseCurrency)}
          testid="ytd-actual"
        />
        <SummaryCard
          label={lang === "es" ? "Desviación" : "Deviation"}
          value={formatMoney(ytd.revenue_diff, baseCurrency)}
          accent={diffAccent}
          testid="ytd-diff"
        />
        <SummaryCard
          label={lang === "es" ? "Food Cost real" : "Actual food cost"}
          value={`${formatMoney(ytd.food_cost_actual_amount, baseCurrency)} · ${ytd.food_cost_pct_actual ?? "—"}%`}
          testid="ytd-food-cost"
        />
        <SummaryCard
          label={lang === "es" ? "Salarios" : "Salaries"}
          value={`${formatMoney(ytd.salary_actual_amount, baseCurrency)} · ${ytd.salary_pct_actual ?? "—"}%`}
          testid="ytd-salary"
        />
        <SummaryCard
          label={lang === "es" ? "Margen bruto" : "Gross margin"}
          value={(() => {
            if (!ytd.revenue_actual) return "—";
            const margin = ytd.revenue_actual - ytd.food_cost_actual_amount - ytd.salary_actual_amount;
            const pct = (margin / ytd.revenue_actual) * 100;
            return `${formatMoney(margin, baseCurrency)} · ${pct.toFixed(1)}%`;
          })()}
          testid="ytd-gross-margin"
          accent={(() => {
            if (!ytd.revenue_actual) return undefined;
            const margin = ytd.revenue_actual - ytd.food_cost_actual_amount - ytd.salary_actual_amount;
            return margin >= 0 ? "positive" : "negative";
          })()}
        />
      </div>
    </div>
  );
}


function SummaryCard({ label, value, accent, testid, valueClass }) {
  const accentClass = valueClass || ({
    positive: "text-[var(--status-positive)]",
    negative: "text-[var(--status-negative)]",
  }[accent]) || "text-[var(--text-primary)]";
  return (
    <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">{label}</p>
      <p className={`font-display text-lg font-bold font-mono-num tracking-tight ${accentClass}`}>{value}</p>
    </div>
  );
}

function BudgetCell({ children, align = "center" }) {
  // iter 82.7n — cells default to center-align so they line up under their
  // center-aligned headers (Objetivo/Real, FC obj/real, Personal obj/real).
  // Old default was "right" which produced ragged pairs.
  const cls = {
    center: "text-center",
    right: "text-right",
    left: "",
  }[align] || "text-center";
  return (
    <td className={`py-2 px-2 ${cls}`}>{children}</td>
  );
}

function BudgetTotalsFooter({ rows, baseCurrency, lang }) {
  const totalTarget = rows.reduce((acc, r) => acc + (Number(r.revenue_target) || 0), 0);
  const totalActual = rows.reduce((acc, r) => acc + (Number(r.revenue_actual) || 0), 0);
  const diff = totalActual - totalTarget;
  const ach = totalTarget > 0 ? Math.round((totalActual / totalTarget) * 100) : null;
  // Weighted-average actuals for FC% and Staff%
  const weightedAvg = (key) => {
    let sum = 0; let weight = 0;
    for (const r of rows) {
      const rev = Number(r.revenue_actual) || 0;
      const pct = r[key];
      if (rev > 0 && pct != null) { sum += pct * rev; weight += rev; }
    }
    return weight > 0 ? sum / weight : null;
  };
  const fcActual = weightedAvg("food_cost_pct_actual");
  const salActual = weightedAvg("salary_pct_actual");
  const fcTargetAvg = (() => {
    const arr = rows.map((r) => r.food_cost_pct_target).filter((v) => v != null);
    return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
  })();
  const salTargetAvg = (() => {
    const arr = rows.map((r) => r.salary_pct_target).filter((v) => v != null);
    return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
  })();
  const achColor = ach == null
    ? "text-[var(--text-muted)]"
    : ach >= 100 ? "text-[var(--status-positive)]"
    : ach >= 90 ? "text-[var(--status-warning)]"
    : "text-[var(--status-negative)]";

  return (
    <tfoot data-testid="budget-totals-footer">
      <tr className="border-t-2 border-[var(--border-color)] bg-[var(--surface-hover)]/40 font-semibold">
        <td className="py-3 px-2 text-left uppercase text-xs tracking-wider">
          {lang === "es" ? "Total año" : "Year total"}
        </td>
        <td className="py-3 px-2 text-center font-mono-num" data-testid="budget-total-target">
          {formatMoney(totalTarget, baseCurrency)}
        </td>
        <td className="py-3 px-2 text-center font-mono-num" data-testid="budget-total-actual">
          {formatMoney(totalActual, baseCurrency)}
        </td>
        <td className="py-3 px-2">
          <div className="flex items-center gap-3">
            <span className={`text-xs font-mono-num font-semibold whitespace-nowrap ${diff >= 0 ? "text-[var(--status-positive)]" : "text-[var(--status-negative)]"}`}>
              {diff >= 0 ? "+" : ""}{formatMoney(diff, baseCurrency)}
            </span>
            <span className={`text-xs font-mono-num whitespace-nowrap ${achColor}`} data-testid="budget-total-achievement">
              {ach == null ? "—" : `${ach}%`}
            </span>
          </div>
        </td>
        <td className="py-3 px-2 text-center font-mono-num text-[var(--text-secondary)]">
          {fcTargetAvg == null ? "—" : `${fcTargetAvg.toFixed(1)}%`}
        </td>
        <td className="py-3 px-2 text-center font-mono-num">
          {fcActual == null ? "—" : `${fcActual.toFixed(1)}%`}
        </td>
        <td className="py-3 px-2 text-center font-mono-num text-[var(--text-secondary)]">
          {salTargetAvg == null ? "—" : `${salTargetAvg.toFixed(1)}%`}
        </td>
        <td className="py-3 px-2 text-center font-mono-num">
          {salActual == null ? "—" : `${salActual.toFixed(1)}%`}
        </td>
      </tr>
    </tfoot>
  );
}

function BudgetRow({ row, monthName, onChange, baseCurrency }) {
  const pct = row.revenue_achievement_pct;
  const barWidth = Math.max(0, Math.min(150, pct ?? 0));
  const barColor =
    pct == null ? "bg-[var(--surface-hover)]" :
      pct >= 100 ? "bg-[var(--status-positive)]" :
        pct >= 90 ? "bg-[var(--status-warning)]" :
          "bg-[var(--status-negative)]";

  const Cell = BudgetCell;

  return (
    <tr className="border-b border-[var(--border-color)] hover:bg-[var(--surface-hover)]" data-testid={`budget-row-${row.month}`}>
      <Cell align="left">
        <span className="font-medium">{monthName}</span>
      </Cell>
      <Cell>
        <Input
          type="number"
          step="any"
          min="0"
          value={row.revenue_target ?? ""}
          onChange={(e) => onChange(row.month, "revenue_target", e.target.value)}
          data-testid={`budget-revenue-target-${row.month}`}
          className="h-8 w-28 mx-auto font-mono-num text-center"
        />
      </Cell>
      <Cell>
        <span className="font-mono-num">{formatMoney(row.revenue_actual, baseCurrency)}</span>
      </Cell>
      <td className="py-2 px-2" data-testid={`budget-progress-${row.month}`}>
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 rounded bg-[var(--surface-hover)] overflow-hidden">
            <div className={`h-full transition-all ${barColor}`} style={{ width: `${Math.min(100, barWidth)}%` }} />
          </div>
          <span className="text-xs font-mono-num w-12 text-right text-[var(--text-secondary)]">
            {pct == null ? "—" : `${pct}%`}
          </span>
        </div>
      </td>
      <Cell>
        <Input
          type="number"
          step="0.1"
          min="0"
          value={row.food_cost_pct_target ?? ""}
          onChange={(e) => onChange(row.month, "food_cost_pct_target", e.target.value)}
          data-testid={`budget-fc-target-${row.month}`}
          className="h-8 w-20 mx-auto font-mono-num text-center"
          placeholder="—"
        />
      </Cell>
      <Cell>
        <span className="font-mono-num text-[var(--text-secondary)]">
          {row.food_cost_pct_actual == null ? "—" : `${row.food_cost_pct_actual.toFixed(1)}%`}
        </span>
        {row.food_cost_pct_deviation != null && (
          <span
            className={`ml-1.5 font-mono-num text-[10px] px-1 rounded ${row.food_cost_pct_deviation > 0 ? "bg-[var(--status-negative-bg)] text-[var(--status-negative)]" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"}`}
            title={row.food_cost_pct_deviation > 0 ? "Por encima del objetivo" : "Por debajo del objetivo"}
            data-testid={`budget-fc-dev-${row.month}`}
          >
            {row.food_cost_pct_deviation > 0 ? "+" : ""}{row.food_cost_pct_deviation.toFixed(1)} pp
          </span>
        )}
      </Cell>
      <Cell>
        <Input
          type="number"
          step="0.1"
          min="0"
          value={row.salary_pct_target ?? ""}
          onChange={(e) => onChange(row.month, "salary_pct_target", e.target.value)}
          data-testid={`budget-sal-target-${row.month}`}
          className="h-8 w-20 mx-auto font-mono-num text-center"
          placeholder="—"
        />
      </Cell>
      <Cell>
        <span className="font-mono-num text-[var(--text-secondary)]">
          {row.salary_pct_actual == null ? "—" : `${row.salary_pct_actual.toFixed(1)}%`}
        </span>
        {row.salary_pct_deviation != null && (
          <span
            className={`ml-1.5 font-mono-num text-[10px] px-1 rounded ${row.salary_pct_deviation > 0 ? "bg-[var(--status-negative-bg)] text-[var(--status-negative)]" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"}`}
            title={row.salary_pct_deviation > 0 ? "Por encima del objetivo" : "Por debajo del objetivo"}
            data-testid={`budget-sal-dev-${row.month}`}
          >
            {row.salary_pct_deviation > 0 ? "+" : ""}{row.salary_pct_deviation.toFixed(1)} pp
          </span>
        )}
      </Cell>
    </tr>
  );
}

function TemplateDialog({ year, onApply, lang }) {
  const [template, setTemplate] = useState("copy_previous_year");
  const [incrementPct, setIncrementPct] = useState("0");
  const [annualTotal, setAnnualTotal] = useState("");
  const [running, setRunning] = useState(false);

  const run = async () => {
    setRunning(true);
    try {
      const payload = {
        year,
        template,
        include_pcts: true,
        increment_pct: parseFloat(incrementPct) || 0,
      };
      if (template !== "copy_previous_year") {
        payload.annual_total = parseFloat(annualTotal) || 0;
      }
      if (template === "seasonal") {
        // Hospitality default weights (lower Jan/Aug, higher Dec)
        payload.seasonal_weights = [0.7, 0.7, 0.9, 1.0, 1.1, 1.2, 1.3, 0.7, 1.0, 1.1, 1.1, 1.4];
      }
      const { data } = await api.post("/budget/template", payload);
      onApply(data.months);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRunning(false);
    }
  };

  return (
    <DialogContent data-testid="budget-template-dialog">
      <DialogHeader>
        <DialogTitle>{lang === "es" ? "Aplicar plantilla" : "Apply template"}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{lang === "es" ? "Estrategia" : "Strategy"}</label>
          <Select value={template} onValueChange={setTemplate}>
            <SelectTrigger data-testid="template-strategy"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="copy_previous_year">
                {lang === "es" ? `Copiar año anterior (${year - 1})` : `Copy previous year (${year - 1})`}
              </SelectItem>
              <SelectItem value="distribute_annual">
                {lang === "es" ? "Repartir total anual ÷ 12" : "Distribute annual ÷ 12"}
              </SelectItem>
              <SelectItem value="seasonal">
                {lang === "es" ? "Estacional (pesos hostelería)" : "Seasonal (hospitality weights)"}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {template === "copy_previous_year" && (
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
              {lang === "es" ? "Incremento % sobre año anterior" : "Year-over-year increment %"}
            </label>
            <Input
              type="number"
              step="0.1"
              value={incrementPct}
              onChange={(e) => setIncrementPct(e.target.value)}
              data-testid="template-increment-pct"
              className="font-mono-num"
              placeholder="0"
            />
            <p className="text-xs text-[var(--text-muted)]">
              {lang === "es" ? "Ej: 10 = +10% sobre lo facturado el año pasado por mes." : "E.g. 10 = +10% over last year's monthly revenue."}
            </p>
          </div>
        )}

        {(template === "distribute_annual" || template === "seasonal") && (
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
              {lang === "es" ? "Total anual objetivo" : "Annual revenue target"}
            </label>
            <Input
              type="number"
              step="any"
              min="0"
              value={annualTotal}
              onChange={(e) => setAnnualTotal(e.target.value)}
              data-testid="template-annual-total"
              className="font-mono-num"
              placeholder="120000"
            />
          </div>
        )}
      </div>
      <DialogFooter>
        <Button
          onClick={run}
          disabled={running}
          data-testid="template-apply-btn"
          className="bg-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/90 text-white"
        >
          <Wand2 size={14} className="mr-1.5" /> {running ? "..." : lang === "es" ? "Aplicar" : "Apply"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
